import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import Logger from '../utils/logger';
import AuthAPI from '../services/api/auth-api';

let logoutTimer;

export const useAuth = () => {
  const [userId, setUserId] = useState(false);
  const [employeeId, setEmployeeId] = useState(false);
  const [userRecordId, setUserRecordId] = useState(false);
  const [userEmail, setUserEmail] = useState(false);
  const [userFirstName, setUserFirstName] = useState(false);
  const [userLastName, setUserLastName] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isTimecard, setIsTimecard] = useState(false);
  const [isWorkflow, setIsWorkflow] = useState(false);
  const [isAnalytics, setIsAnalytics] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [homeUnion, setHomeUnion] = useState(null);
  const [resetPass, setResetPass] = useState(false);
  const [token, setToken] = useState(false);
  const [tokenExpirationDate, setTokenExpirationDate] = useState();

  const login = useCallback(
    (
      uid,
      email,
      token,
      tokenExpires,
      firstName,
      lastName,
      isAdmin,
      isTimecard,
      isWorkflow,
      isAnalytics,
      isSuperAdmin,
      resetPass,
      employeeId,
      userRecordId,
      homeUnion
    ) => {
      Logger.debug('Token expiration set', { expiresAt: tokenExpires });

      setUserId(uid);
      setEmployeeId(employeeId || uid);
      setUserRecordId(userRecordId);
      setUserEmail(email);
      setUserFirstName(firstName);
      setUserLastName(lastName);
      setIsAdmin(isAdmin);
      setIsTimecard(isTimecard);
      setIsWorkflow(isWorkflow);
      setIsAnalytics(isAnalytics);
      setIsSuperAdmin(!!isSuperAdmin);
      setHomeUnion(homeUnion);
      setResetPass(resetPass);
      setToken(token);
      setTokenExpirationDate(tokenExpires);

      localStorage.setItem(
        'userData',
        JSON.stringify({
          userId: uid,
          employeeId: employeeId || uid,
          userRecordId: userRecordId,
          email: email,
          token: token,
          tokenExpires: tokenExpires.toISOString(),
          firstName: firstName,
          lastName: lastName,
          isAdmin: isAdmin,
          isTimecard: isTimecard,
          isWorkflow: isWorkflow,
          isAnalytics: isAnalytics,
          isSuperAdmin: !!isSuperAdmin,
          homeUnion: homeUnion,
          reset_password: resetPass,
        })
      );
    },
    []
  );

  const logout = useCallback(async () => {
    try {
      const logoutResponse = await AuthAPI.logout();
      Logger.info('Logout successful', {
        message: logoutResponse.messageResponse || logoutResponse.message,
      });
    } catch (err) {
      // Log error but still proceed with logout (clear local state)
      Logger.error('Error calling logout endpoint', err);
    }

    setUserId(null);
    setEmployeeId(null);
    setUserRecordId(null);
    setUserEmail(null);
    setUserFirstName(null);
    setUserLastName(null);
    setIsAdmin(null);
    setIsTimecard(null);
    setIsWorkflow(null);
    setIsAnalytics(null);
    setIsSuperAdmin(false);
    setHomeUnion(null);
    setResetPass(false);
    setToken(null);
    setTokenExpirationDate(null);
    localStorage.removeItem('userData');
  }, []);

  useEffect(() => {
    if (token && tokenExpirationDate) {
      const remainingTime =
        tokenExpirationDate.getTime() - new Date().getTime();
      Logger.debug('Token expiration timer set', {
        remainingTimeMs: remainingTime,
        remainingTimeMinutes: remainingTime / 1000 / 60,
      });
      logoutTimer = setTimeout(logout, remainingTime);
    } else {
      clearTimeout(logoutTimer);
    }
  }, [token, logout, tokenExpirationDate]);

  useEffect(() => {
    const storedData = JSON.parse(localStorage.getItem('userData'));
    const currentTime = new Date();
    Logger.debug('Checking stored auth data', { currentTime });
    if (
      storedData &&
      storedData.token &&
      new Date(storedData.tokenExpires) > currentTime
    ) {
      login(
        storedData.userId,
        storedData.email,
        storedData.token,
        new Date(storedData.tokenExpires),
        storedData.firstName,
        storedData.lastName,
        storedData.isAdmin,
        storedData.isTimecard ?? storedData.isForeman,
        storedData.isWorkflow ?? false,
        storedData.isAnalytics ?? false,
        storedData.isSuperAdmin ?? false,
        storedData.reset_password,
        storedData.employeeId,
        storedData.userRecordId,
        storedData.homeUnion
      );
    }
  }, [login]);

  return {
    token,
    login,
    logout,
    userId,
    employeeId,
    userRecordId,
    userEmail,
    userFirstName,
    userLastName,
    isAdmin,
    isTimecard,
    isWorkflow,
    isAnalytics,
    isSuperAdmin,
    homeUnion,
    resetPass,
    setResetPass,
  };
};
